const domainHashCache = new Map<string, boolean>()

/**
 * Hash a domain string using SHA-256 (async, uses Web Crypto API)
 * @param domain - The domain string to hash
 * @returns Promise<string> - The hex-encoded hash
 */
export const hashDomain = async (domain: string): Promise<string> => {
  const encoder = new TextEncoder()
  const data = encoder.encode(domain.toLowerCase().trim())
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
}

/**
 * Check if an email domain matches any of the hashed domains
 * Uses Web Crypto API for SHA-256 hashing to match pre-computed hashes
 * @param email - The email address to check
 * @param hashedDomains - Array of hashed domain strings (SHA-256 hashes)
 * @returns Promise<boolean> - True if the email domain matches any hashed domain
 */
export const isLegacyEmailDomain = async (
  email: string | undefined,
  hashedDomains: string[]
): Promise<boolean> => {
  if (!email) return false

  // Check cache first
  const cacheKey = email.toLowerCase()
  if (domainHashCache.has(cacheKey)) {
    return domainHashCache.get(cacheKey)!
  }

  // Extract domain from email (everything after @)
  const atIndex = email.indexOf('@')
  if (atIndex === -1) {
    domainHashCache.set(cacheKey, false)
    return false
  }

  const domain = '@' + email.substring(atIndex + 1).toLowerCase()
  const domainHash = await hashDomain(domain)
  const isLegacy = hashedDomains.includes(domainHash)

  // Cache the result
  domainHashCache.set(cacheKey, isLegacy)
  return isLegacy
}
